// ROSections.jsx - Individual sections for the RO Detail Page
import React, { useState } from 'react';
import api from '../../services/api';
import { VENDOR_TYPES, PART_STATUSES, VENDOR_STATUSES, INTERNAL_RATE, RO_STATUSES } from './constants';
import { SectionHeader, FileUpload, PhotoGallery, DateInput } from './shared';

// ============ SECTION C: Work Items ============
export const WorkItemsSection = ({ ro, onRefresh, showCosts }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', estimatedHours: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const addWorkItem = async () => {
    if (!newItem.description.trim()) {
      alert('Description is required');
      return;
    }
    setSaving(true);
    try {
      await api.addROWorkItem(ro.id, {
        description: newItem.description,
        estimatedHours: parseFloat(newItem.estimatedHours) || 0,
        estimatedCost: (parseFloat(newItem.estimatedHours) || 0) * INTERNAL_RATE,
        notes: newItem.notes
      });
      setNewItem({ description: '', estimatedHours: '', notes: '' });
      setShowAddForm(false);
      onRefresh();
    } catch (err) {
      alert('Failed to add work item: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (item) => {
    let actualHours = null;
    if (!item.completed) {
      const input = prompt('Enter actual hours spent:', item.estimatedHours || '0');
      if (input === null) return; // User cancelled
      actualHours = parseFloat(input) || 0;
    }
    
    try {
      await api.updateROWorkItem(ro.id, item.id, {
        completed: !item.completed,
        actualHours: actualHours
      });
      onRefresh();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const deleteWorkItem = async (itemId) => {
    if (!window.confirm('Delete this work item?')) return;
    try {
      await api.deleteROWorkItem(ro.id, itemId);
      onRefresh();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const totalEstHours = ro.workItems?.reduce((sum, w) => sum + parseFloat(w.estimatedHours || 0), 0) || 0;
  const totalActualHours = ro.workItems?.reduce((sum, w) => sum + parseFloat(w.actualHours || 0), 0) || 0;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <SectionHeader
        letter="C"
        title="Labor for Work Needed"
        action={
          <button
            onClick={() => setShowAddForm(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            + Add Work Item
          </button>
        }
      />

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Description *</label>
              <input
                type="text"
                value={newItem.description}
                onChange={e => setNewItem(n => ({ ...n, description: e.target.value }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                placeholder="What work needs to be done?"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Estimated Hours</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={newItem.estimatedHours}
                  onChange={e => setNewItem(n => ({ ...n, estimatedHours: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <input
                  type="text"
                  value={newItem.notes}
                  onChange={e => setNewItem(n => ({ ...n, notes: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowAddForm(false); setNewItem({ description: '', estimatedHours: '', notes: '' }); }}
                className="px-3 py-1.5 text-neutral-600 hover:bg-neutral-100 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={addWorkItem}
                disabled={saving}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Work Items List */}
      <div className="space-y-2">
        {ro.workItems?.length > 0 ? (
          ro.workItems.map(item => (
            <div
              key={item.id}
              className={`flex items-start gap-3 p-3 rounded-lg border transition ${
                item.completed 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-white border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <input
                type="checkbox"
                checked={item.completed || false}
                onChange={() => toggleComplete(item)}
                className="w-5 h-5 mt-0.5 rounded cursor-pointer"
              />
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${item.completed ? 'line-through text-neutral-400' : ''}`}>
                  {item.description}
                </div>
                <div className="text-xs text-neutral-500 mt-1 flex flex-wrap gap-x-3">
                  {item.estimatedHours > 0 && (
                    <span>Est: {item.estimatedHours}h</span>
                  )}
                  {item.actualHours > 0 && (
                    <span>Actual: {item.actualHours}h</span>
                  )}
                  {showCosts && item.estimatedHours > 0 && (
                    <span className="text-green-600">${(item.estimatedHours * INTERNAL_RATE).toFixed(2)}</span>
                  )}
                  {item.notes && (
                    <span className="text-neutral-400">• {item.notes}</span>
                  )}
                  {item.completedByName && (
                    <span className="text-green-600">✓ by {item.completedByName}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => deleteWorkItem(item.id)}
                className="text-red-400 hover:text-red-600 text-lg px-1"
                title="Delete"
              >
                ×
              </button>
            </div>
          ))
        ) : (
          <div className="text-center text-neutral-400 py-8">
            No work items yet. Click "+ Add Work Item" to add one.
          </div>
        )}
      </div>

      {/* Totals */}
      {ro.workItems?.length > 0 && (
        <div className="mt-4 pt-3 border-t flex justify-end gap-6 text-sm">
          <span>Est: <strong>{totalEstHours.toFixed(1)}h</strong></span>
          <span>Actual: <strong>{totalActualHours.toFixed(1)}h</strong></span>
          {showCosts && (
            <span>Cost: <strong className="text-green-600">${(totalEstHours * INTERNAL_RATE).toFixed(2)}</strong></span>
          )}
        </div>
      )}
    </div>
  );
};

// ============ SECTION D: Parts Needed ============
export const PartsSection = ({ ro, vendors, onRefresh, showCosts, onSyncPrecheck }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPart, setNewPart] = useState({
    description: '',
    partNumber: '',
    quantity: 1,
    unitCost: '',
    vendorName: '',
    orderDate: '',
    eta: '',
    trackingUrl: '',
    status: 'needed',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const addPart = async () => {
    if (!newPart.description.trim()) {
      alert('Part name is required');
      return;
    }
    setSaving(true);
    try {
      await api.addRepairOrderPart(ro.id, {
        ...newPart,
        quantity: parseInt(newPart.quantity) || 1,
        unitCost: parseFloat(newPart.unitCost) || 0
      });
      setNewPart({
        description: '',
        partNumber: '',
        quantity: 1,
        unitCost: '',
        vendorName: '',
        orderDate: '',
        eta: '',
        trackingUrl: '',
        status: 'needed',
        notes: ''
      });
      setShowAddForm(false);
      onRefresh();
    } catch (err) {
      alert('Failed to add part: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updatePartStatus = async (partId, status) => {
    try {
      await api.updateRepairOrderPart(ro.id, partId, { status });
      onRefresh();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const updatePartField = async (partId, field, value) => {
    try {
      // Normalize numeric fields before sending to API
      let payloadValue = value;
      if (field === 'quantity') {
        payloadValue = parseInt(value, 10) || 1;
      } else if (field === 'unitCost') {
        payloadValue = parseFloat(value) || 0;
      }

      await api.updateRepairOrderPart(ro.id, partId, { [field]: payloadValue });
      onRefresh();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const deletePart = async (partId) => {
    if (!window.confirm('Delete this part?')) return;
    try {
      await api.deleteRepairOrderPart(ro.id, partId);
      onRefresh();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const setPartReminder = async (part) => {
    const reminderDate = prompt('Set reminder date (YYYY-MM-DD):', part.eta?.split('T')[0] || new Date().toISOString().split('T')[0]);
    if (!reminderDate) return;
    
    try {
      await api.createTask({
        vehicleId: ro.vehicleId,
        title: `📦 Part Reminder: ${part.description}`,
        description: `Check on part order for RO ${ro.roNumber}:\n- Part: ${part.description}\n- Vendor: ${part.vendorName || 'N/A'}\n- ETA: ${part.eta?.split('T')[0] || 'Not set'}`,
        dueDate: reminderDate,
        assignedRole: 'shop',
        priority: 'normal',
        status: 'pending'
      });
      alert(`Reminder set for ${reminderDate}`);
    } catch (err) {
      alert('Failed to set reminder: ' + err.message);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const totalCost = ro.parts?.reduce((sum, p) => sum + parseFloat(p.totalCost || 0), 0) || 0;
  
  // Debug: Log parts data
  console.log('PartsSection - RO parts data:', ro.parts);
  console.log('PartsSection - Full RO object:', ro);

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <SectionHeader
        letter="D"
        title="Parts Needed"
        action={
          <div className="flex gap-2">
            {ro.vehicleId && (
              <button
                onClick={onSyncPrecheck}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Sync Precheck Parts
              </button>
            )}
            <button
              onClick={() => setShowAddForm(true)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              + Add Part
            </button>
          </div>
        }
      />

      {/* Add Form - Simplified */}
      {showAddForm && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium mb-1">Part Name *</label>
              <input
                type="text"
                value={newPart.description}
                onChange={e => setNewPart(n => ({ ...n, description: e.target.value }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                placeholder="e.g., Brake Pads, O2 Sensor"
                autoFocus
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium mb-1">Vendor/Source</label>
              <input
                type="text"
                value={newPart.vendorName}
                onChange={e => setNewPart(n => ({ ...n, vendorName: e.target.value }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                placeholder="e.g., AutoZone, Amazon, RockAuto"
              />
            </div>
            {showCosts && (
              <div>
                <label className="block text-sm font-medium mb-1">Cost $</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newPart.unitCost}
                  onChange={e => setNewPart(n => ({ ...n, unitCost: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  placeholder="0.00"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={newPart.status}
                onChange={e => setNewPart(n => ({ ...n, status: e.target.value, orderDate: e.target.value === 'ordered' ? new Date().toISOString().split('T')[0] : n.orderDate }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white"
              >
                {PART_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <DateInput
              label="Est. Delivery"
              value={newPart.eta}
              onChange={v => setNewPart(n => ({ ...n, eta: v }))}
            />
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <input
                type="text"
                value={newPart.notes}
                onChange={e => setNewPart(n => ({ ...n, notes: e.target.value }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                placeholder="Optional notes..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 mt-3 border-t">
            <button
              onClick={() => { setShowAddForm(false); }}
              className="px-3 py-1.5 text-neutral-600 hover:bg-neutral-100 rounded-lg text-sm"
            >
              Cancel
            </button>
            <button
              onClick={addPart}
              disabled={saving}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Part'}
            </button>
          </div>
        </div>
      )}


      {/* Parts Table - Simplified */}
      {ro.parts?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b">
                <th className="py-2 pr-2">Part</th>
                <th className="py-2 pr-2">Vendor</th>
                {showCosts && <th className="py-2 pr-2">Cost</th>}
                <th className="py-2 pr-2">ETA</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {ro.parts.map(part => {
                const etaDate = part.eta?.split('T')[0];
                const isOverdue = etaDate && etaDate < today && !['arrived', 'installed'].includes(part.status);
                
                return (
                  <tr key={part.id} className={`border-b border-neutral-100 ${isOverdue ? 'bg-red-50' : ''}`}>
                    <td className="py-3 pr-2">
                      <input
                        type="text"
                        className="w-full border border-neutral-200 rounded px-2 py-1 text-sm font-medium"
                        value={part.description || ''}
                        onChange={e => updatePartField(part.id, 'description', e.target.value)}
                        placeholder="Part name"
                      />
                      {part.notes && (
                        <div className="text-xs text-neutral-500 mt-1 truncate" title={part.notes}>{part.notes}</div>
                      )}
                    </td>
                    <td className="py-3 pr-2 text-xs">
                      <input
                        type="text"
                        className="w-full border border-neutral-200 rounded px-2 py-1 text-xs"
                        value={part.vendorName || ''}
                        onChange={e => updatePartField(part.id, 'vendorName', e.target.value)}
                        placeholder="Vendor"
                      />
                    </td>
                    {showCosts && (
                      <td className="py-3 pr-2 font-mono text-xs">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-20 border border-neutral-200 rounded px-2 py-1 text-xs"
                          value={part.unitCost ?? ''}
                          onChange={e => updatePartField(part.id, 'unitCost', e.target.value)}
                          placeholder="$0"
                        />
                      </td>
                    )}
                    <td className="py-3 pr-2">
                      <input
                        type="date"
                        value={etaDate || ''}
                        onChange={e => updatePartField(part.id, 'eta', e.target.value)}
                        className={`text-xs border rounded px-2 py-1 w-28 ${isOverdue ? 'border-red-300 bg-red-50' : 'border-neutral-200'}`}
                      />
                      {isOverdue && <span className="text-red-600 ml-1">⚠</span>}
                    </td>
                    <td className="py-3 pr-2">
                      <select
                        value={part.status || 'needed'}
                        onChange={e => updatePartStatus(part.id, e.target.value)}
                        className="text-xs border border-neutral-200 rounded px-2 py-1 bg-white"
                      >
                        {PART_STATUSES.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3">
                      <div className="flex gap-1">
                        <button
                          onClick={() => setPartReminder(part)}
                          className="text-blue-400 hover:text-blue-600 text-sm"
                          title="Set Reminder"
                        >
                          🔔
                        </button>
                        <button
                          onClick={() => deletePart(part.id)}
                          className="text-red-400 hover:text-red-600 text-lg"
                          title="Delete"
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-neutral-400 py-8">
          No parts needed yet. Click "+ Add Part" to add one.
        </div>
      )}

      {/* Total */}
      {showCosts && ro.parts?.length > 0 && (
        <div className="mt-4 pt-3 border-t flex justify-end text-sm">
          <span>Total Parts Cost: <strong className="text-green-600">${totalCost.toFixed(2)}</strong></span>
        </div>
      )}
    </div>
  );
};

// ============ SECTION E: Outside Vendors ============
export const VendorsSection = ({ ro, vendors, onRefresh, showCosts }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVendor, setNewVendor] = useState({
    vendorType: '',
    vendorName: '',
    estimatedCost: '',
    scheduledDate: '',
    status: 'pending',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const addVendor = async () => {
    if (!newVendor.vendorType) {
      alert('Please select a vendor type');
      return;
    }
    setSaving(true);
    try {
      await api.addROVendor(ro.id, {
        ...newVendor,
        estimatedCost: parseFloat(newVendor.estimatedCost) || 0
      });
      setNewVendor({
        vendorType: '',
        vendorName: '',
        estimatedCost: '',
        scheduledDate: '',
        status: 'pending',
        notes: ''
      });
      setShowAddForm(false);
      onRefresh();
    } catch (err) {
      alert('Failed to add vendor: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateVendorStatus = async (vendorId, newStatus) => {
    try {
      let actualCost = null;
      if (newStatus === 'complete') {
        const vendor = ro.outsideVendors?.find(v => v.id === vendorId);
        const suggested = vendor?.estimatedCost || vendor?.actualCost || 0;
        const input = prompt('Enter actual cost for this outside service:', suggested ? String(suggested) : '');
        if (input === null) return; // User cancelled
        actualCost = parseFloat(String(input).replace(/[$,]/g, '')) || 0;
      }
      await api.updateROVendor(ro.id, vendorId, { status: newStatus, actualCost });
      onRefresh();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const updateVendorField = async (vendorId, field, value) => {
    try {
      await api.updateROVendor(ro.id, vendorId, { [field]: value });
      onRefresh();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const deleteVendor = async (vendorId) => {
    if (!window.confirm('Remove this vendor?')) return;
    try {
      await api.deleteROVendor(ro.id, vendorId);
      onRefresh();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const totalCost = ro.outsideVendors?.reduce((sum, v) => sum + parseFloat(v.actualCost || v.estimatedCost || 0), 0) || 0;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <SectionHeader
        letter="E"
        title="Outside Vendors"
        action={
          <button
            onClick={() => setShowAddForm(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            + Add Vendor
          </button>
        }
      />

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Vendor Type *</label>
              <select
                value={newVendor.vendorType}
                onChange={e => setNewVendor(n => ({ ...n, vendorType: e.target.value }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white"
              >
                <option value="">Select type...</option>
                {VENDOR_TYPES.map(vt => (
                  <option key={vt.value} value={vt.value}>{vt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Vendor Name</label>
              <input
                type="text"
                value={newVendor.vendorName}
                onChange={e => setNewVendor(n => ({ ...n, vendorName: e.target.value }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                placeholder="Optional - specific vendor"
              />
            </div>
            {showCosts && (
              <div>
                <label className="block text-sm font-medium mb-1">Estimated Cost $</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newVendor.estimatedCost}
                  onChange={e => setNewVendor(n => ({ ...n, estimatedCost: e.target.value }))}
                  className="w-full h-10 px-3 border border-neutral-200 rounded-lg"
                  placeholder="0.00"
                />
              </div>
            )}
            <DateInput
              label="Scheduled Date"
              value={newVendor.scheduledDate}
              onChange={v => setNewVendor(n => ({ ...n, scheduledDate: v }))}
            />
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Notes / Work Description</label>
              <textarea
                value={newVendor.notes}
                onChange={e => setNewVendor(n => ({ ...n, notes: e.target.value }))}
                className="w-full h-20 px-3 py-2 border border-neutral-200 rounded-lg resize-none"
                placeholder="Describe the work needed..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 mt-3 border-t">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-3 py-1.5 text-neutral-600 hover:bg-neutral-100 rounded-lg text-sm"
            >
              Cancel
            </button>
            <button
              onClick={addVendor}
              disabled={saving}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Vendor'}
            </button>
          </div>
        </div>
      )}

      {/* Vendors List */}
      {ro.outsideVendors?.length > 0 ? (
        <div className="space-y-3">
          {ro.outsideVendors.map(vendor => {
            const typeConfig = VENDOR_TYPES.find(t => t.value === vendor.vendorType);
            return (
              <div
                key={vendor.id}
                className={`p-3 rounded-lg border ${
                  vendor.status === 'complete' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-white border-neutral-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{typeConfig?.label || vendor.vendorType}</span>
                      {vendor.vendorName && (
                        <span className="text-sm text-neutral-500">• {vendor.vendorName}</span>
                      )}
                    </div>
                    {vendor.notes && (
                      <div className="text-sm text-neutral-600 mb-2">{vendor.notes}</div>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs">
                      {showCosts && (vendor.actualCost > 0 || vendor.estimatedCost > 0) && (
                        <span className="text-green-600 font-medium">
                          ${parseFloat(vendor.actualCost || vendor.estimatedCost || 0).toFixed(2)}
                          {vendor.status === 'complete' && vendor.actualCost > 0 && <span className="text-neutral-500 ml-1">(actual)</span>}
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="text-neutral-500">Scheduled:</span>
                        <input
                          type="date"
                          value={vendor.scheduledDate?.split('T')[0] || ''}
                          onChange={e => updateVendorField(vendor.id, 'scheduledDate', e.target.value)}
                          className="border border-neutral-200 rounded px-2 py-0.5 text-xs"
                        />
                      </div>
                      <select
                        value={vendor.status || 'pending'}
                        onChange={e => updateVendorStatus(vendor.id, e.target.value)}
                        className="border border-neutral-200 rounded px-2 py-0.5 text-xs bg-white"
                      >
                        {VENDOR_STATUSES.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteVendor(vendor.id)}
                    className="text-red-400 hover:text-red-600 text-lg"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center text-neutral-400 py-8">
          No outside vendors needed. Click "+ Add Vendor" to add one.
        </div>
      )}

      {/* Total */}
      {showCosts && ro.outsideVendors?.length > 0 && (
        <div className="mt-4 pt-3 border-t flex justify-end text-sm">
          <span>Total Vendor Cost: <strong className="text-green-600">${totalCost.toFixed(2)}</strong></span>
        </div>
      )}
    </div>
  );
};

// ============ SECTION H: Timeline/History ============
export const TimelineSection = ({ ro }) => {
  const history = ro.history || [];

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <SectionHeader letter="H" title="Timeline / Audit Log" />
      
      <div className="max-h-64 overflow-y-auto">
        {history.length > 0 ? (
          <div className="space-y-2">
            {history.map(entry => (
              <div
                key={entry.id}
                className="text-sm border-l-2 border-neutral-200 pl-3 py-1"
              >
                <span className="text-neutral-500">
                  {new Date(entry.createdAt).toLocaleString()}
                </span>
                {' - '}
                <span className="font-medium">{entry.userName || 'System'}</span>
                {': '}
                <span>{entry.action}</span>
                {entry.fieldName && (
                  <span className="text-neutral-500"> ({entry.fieldName})</span>
                )}
                {entry.newValue && (
                  <span className="text-blue-600"> → "{entry.newValue}"</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-neutral-400 text-sm text-center py-8">
            No history recorded yet
          </div>
        )}
      </div>
    </div>
  );
};
// Force rebuild Fri Jan  9 18:57:36 EST 2026
